import { ref, onMounted } from 'vue';
import ModuleInfo from 'Components/types/ModuleInfo.js';
import { useUserSettingsStore } from 'Js/store/useUserSettingsStore';

const recentlyViewed = ref([]);
const recentlyViewedCount = ref(5);
let loadedStorageKey = null;

/**
 * This composable is used to manage the recently viewed modules
 * It stores the recently viewed modules in the local storage,
 * and returns the list of recently viewed modules and a function to add a module to the list.
 * @returns
 */
function getUserId() {
    return useUserSettingsStore().state?.UserId ?? null;
}

function getStorageKey() {
    return `recentlyViewed_${getUserId()}`;
}

function getStorageKeyForCount() {
    return `recentlyViewedNumber_${getUserId()}`;
}

function persistRecentlyViewed() {
    localStorage.setItem(getStorageKey(), JSON.stringify(recentlyViewed.value));
}

function normalizeRecentlyViewedUrl(url) {
    try {
        const parsedUrl = new URL(url, window.location.origin);
        if (parsedUrl.origin === window.location.origin) {
            return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
        }
    } catch {
        return url;
    }

    return url;
}

/**
 * Builds a deduplication key that ignores the EnPower account segment,
 * so the same module viewed under different accounts (e.g. enloc-energy vs knauber)
 * is treated as a single recently-viewed entry.
 * $1@EnPowerAccountId is used as a placeholder for the account segment.
 * For example, /EnPower/enloc-energy/contracts and /EnPower/knauber/contracts both will produce /EnPower/@EnPowerAccountId/contracts
 */
function getDeduplicationKey(url) {
    const normalized = normalizeRecentlyViewedUrl(url);
    if (!normalized) return normalized;
    return normalized.replace(/(\/EnPower\/)[^/]+/i, '$1@EnPowerAccountId');
}

function setNumberRecentlyViewedModules(count) {
    // Ensure the stored value is always a number so v-model comparisons match
    let num = Number(count);
    if (Number.isNaN(num)) {
        num = 5;
    }
    localStorage.setItem(getStorageKeyForCount(), String(num));
    recentlyViewedCount.value = num;
}

function loadRecentlyViewed() {
    const storageKey = getStorageKey();
    if (loadedStorageKey === storageKey) {
        return;
    }

    loadedStorageKey = storageKey;

    const storedRecentlyViewed = localStorage.getItem(storageKey);
    if (storedRecentlyViewed) {
        try {
            const parsedRecentlyViewed = JSON.parse(storedRecentlyViewed);
            const items = Array.isArray(parsedRecentlyViewed) ? parsedRecentlyViewed : [];
            const seen = new Set();
            recentlyViewed.value = items.reduce((acc, item) => {
                const normalizedUrl = normalizeRecentlyViewedUrl(item.url);
                if (!normalizedUrl) return acc;
                const deduplicationKey = getDeduplicationKey(item.url);
                if (seen.has(deduplicationKey)) return acc;
                seen.add(deduplicationKey);
                acc.push({ ...item, url: normalizedUrl });
                return acc;
            }, []);
        } catch {
            recentlyViewed.value = [];
        }
    } else {
        recentlyViewed.value = [];
    }

    const storedCount = localStorage.getItem(getStorageKeyForCount());
    if (storedCount) {
        setNumberRecentlyViewedModules(storedCount);
    }
}

/**
 *
 * @param {ModuleInfo} module
 */
function addToRecentlyViewed(module) {
    if (!module?.url) {
        return;
    }
    loadRecentlyViewed();

    const normalizedUrl = normalizeRecentlyViewedUrl(module.url);
    const deduplicationKey = getDeduplicationKey(module.url);
    recentlyViewed.value = recentlyViewed.value.filter(item => getDeduplicationKey(item.url) !== deduplicationKey);
    recentlyViewed.value.unshift(new ModuleInfo({
        area: module.area,
        name: module.name,
        icon: module.icon,
        url: normalizedUrl
    }));
    persistRecentlyViewed();
}

export function addRecentlyViewedModule(module) {
    addToRecentlyViewed(module);
}

export function useRecentlyViewed() {
    onMounted(() => {
        loadRecentlyViewed();
    });

    return {recentlyViewed, addToRecentlyViewed, setNumberRecentlyViewedModules, recentlyViewedCount};
}
